import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getClientRegistre, advanceAppointment, contractTypeOf, type RdvRow } from '../../lib/refonteApi';
import { NqReasonModal } from '../../components/refonte/NqReasonPicker';
import { LIFECYCLE, allowedTransitions, type LifecycleStatus } from '../../lib/appointmentLifecycle';
import { useAuth } from '../../components/AuthProvider';
import { usePortalScope } from './portal/usePortalScope';
import StatusBadge from '../../components/refonte/StatusBadge';
import ViewAsClientToggle from '../../components/refonte/ViewAsClientToggle';
import AddRdvModal from '../../components/refonte/AddRdvModal';
import { SkeletonRows, Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

// Statuts qu'on peut poser vite depuis le registre (interne) : issue + qualification.
const QUICK: LifecycleStatus[] = ['held', 'no_show', 'qualified', 'not_qualified'];
const QUICK_SHORT: Partial<Record<LifecycleStatus, string>> = {
  held: '✓ Honoré', no_show: 'No-show', qualified: '✓ Qualifié', not_qualified: 'Non qualifié',
};

const fmtD = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');

// Registre des rendez-vous — table complète du client, filtrable, pleine largeur.
// Chaque ligne ouvre la fiche détail. Vocabulaire dictionnaire (booké/honoré/qualifié).
const PortalRegistrePage: React.FC = () => {
  const scope = usePortalScope();
  const { clientId, clientName } = scope;
  const navigate = useNavigate();
  const { profile } = useAuth();
  const role = profile?.role || 'client';
  const isInternal = role !== 'client'; // statuation rapide réservée à l'interne

  const [rows, setRows] = useState<(RdvRow & { created_at?: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | LifecycleStatus>('all');
  const [sdrFilter, setSdrFilter] = useState<'all' | string>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try { setRows(await getClientRegistre(clientId)); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const [nqTarget, setNqTarget] = useState<RdvRow | null>(null);
  const submitNq = async (reason: string, note?: string) => {
    if (!nqTarget) return;
    setActing(nqTarget.id); setErr(null);
    try { await advanceAppointment(nqTarget.id, 'not_qualified', { reason, note }); setNqTarget(null); await load(); }
    catch (e: any) { setErr(e.message); }
    setActing(null);
  };

  // Statuer un RDV en un clic depuis le registre (interne). Motif requis pour « non qualifié ».
  const quickAct = async (e: React.MouseEvent, rdv: RdvRow, to: LifecycleStatus) => {
    e.stopPropagation();
    setActing(rdv.id); setErr(null);
    try {
      // motif « non qualifié » = liste fermée → modale dédiée (plus de texte libre, audit 08/10)
      if (to === 'not_qualified') { setNqTarget(rdv); setActing(null); return; }
      await advanceAppointment(rdv.id, to, { icpConformant: to === 'qualified' ? true : undefined });
      await load();
    } catch (e: any) { setErr(e.message); }
    setActing(null);
  };

  const cQuery = clientId ? `?c=${clientId}${scope.viewAsClient ? '&asclient=1' : ''}` : (scope.viewAsClient ? '?asclient=1' : '');
  const showStatuer = isInternal && !scope.viewAsClient; // masquée en preview « vue client »
  // Filtre SDR (interne) : liste des SDR présents dans le registre + tri (demande Romain 30/09).
  const sdrNames = isInternal ? Array.from(new Set(rows.map(r => r.sdr?.sales_name).filter(Boolean) as string[])).sort() : [];
  const filtered = rows.filter(r =>
    (status === 'all' || r.lifecycle_status === status) &&
    (sdrFilter === 'all' || r.sdr?.sales_name === sdrFilter) &&
    (!q || `${r.prospect_name ?? ''} ${r.company_name ?? ''}`.toLowerCase().includes(q.toLowerCase())));

  // Statuts réellement présents (pour ne proposer que des filtres utiles), avec compte.
  const counts = rows.reduce<Record<string, number>>((m, r) => {
    if (r.lifecycle_status) m[r.lifecycle_status] = (m[r.lifecycle_status] ?? 0) + 1;
    return m;
  }, {});
  const present = Object.keys(counts) as LifecycleStatus[];

  return (
    <div>
      {nqTarget && <NqReasonModal busy={acting === nqTarget.id} onSubmit={submitNq} onCancel={() => setNqTarget(null)} />}
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Registre des rendez-vous</h1>
        <div className="flex items-center gap-3">
          <Link to={`/app/portail${cQuery}`} className="text-[12px] font-semibold text-dim hover:text-ink">← Tableau de bord</Link>
          {scope.isInternal && <ViewAsClientToggle on={scope.viewAsClient} onChange={scope.setViewAsClient} />}
          {(scope.isInternal || scope.options.length > 1) && (
            <select value={clientId ?? ''} onChange={e => scope.setClientId(e.target.value || null)}
              className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[220px]">
              {!clientId && <option value="">— choisir —</option>}
              {scope.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          {showStatuer && clientId && (
            <button onClick={() => setAddOpen(true)} className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-hook-rose text-white">+ Ajouter un RDV</button>
          )}
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">
        Tous vos rendez-vous et leur statut · clique une ligne pour le détail (conversation, infos prospect)
        {clientName && <span className="text-faint"> · {clientName}</span>}
      </p>

      {(err || scope.error) && <EmptyState tone="error" title="Chargement impossible" hint={err || scope.error || ''} className="mb-4" />}

      {scope.resolving ? <Skeleton className="h-[60vh] rounded-card" /> : !clientId ? (
        <EmptyState title="Aucun client rattaché" hint="Votre compte n'est associé à aucun client." />
      ) : (
        <div className="bg-panel border border-line rounded-card p-5">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher prospect / société…"
              className="rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] w-64" />
            <button onClick={() => setStatus('all')}
              className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${status === 'all' ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>Tous ({rows.length})</button>
            {present.map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${status === s ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{LIFECYCLE[s].label} ({counts[s]})</button>
            ))}
            {isInternal && sdrNames.length > 1 && (
              <select value={sdrFilter} onChange={e => setSdrFilter(e.target.value)} className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[11.5px]">
                <option value="all">Tous les SDR</option>
                {sdrNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
            <span className="ml-auto font-mono text-[11px] text-faint">{filtered.length} affiché{filtered.length > 1 ? 's' : ''}</span>
          </div>

          {loading ? <SkeletonRows count={8} /> : filtered.length === 0 ? (
            <EmptyState title="Aucun rendez-vous" hint="Aucun rendez-vous ne correspond à ce filtre." />
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-line-2 font-mono text-[9.5px] uppercase tracking-widest text-faint">
                    <th className="py-2 px-2 font-semibold">Prospect</th>
                    <th className="py-2 px-2 font-semibold">Société</th>
                    <th className="py-2 px-2 font-semibold hidden md:table-cell">Fonction</th>
                    <th className="py-2 px-2 font-semibold whitespace-nowrap">Date RDV</th>
                    <th className="py-2 px-2 font-semibold whitespace-nowrap hidden sm:table-cell">Pris le</th>
                    {isInternal && <th className="py-2 px-2 font-semibold whitespace-nowrap">SDR</th>}
                    <th className="py-2 px-2 font-semibold">Statut</th>
                    {showStatuer && <th className="py-2 px-2 font-semibold">Statuer</th>}
                    <th className="py-2 px-2 w-6"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {filtered.map(rdv => {
                    const quick = showStatuer
                      ? allowedTransitions(rdv.lifecycle_status ?? null, role, contractTypeOf(rdv)).filter(t => QUICK.includes(t))
                      : [];
                    return (
                    <tr key={rdv.id} onClick={() => navigate(`/app/portail/rdv/${rdv.id}${cQuery}`)}
                      className="cursor-pointer hover:bg-panel-2 transition-colors">
                      <td className="py-2.5 px-2 text-[13px] font-semibold">{rdv.prospect_name || 'Prospect'}</td>
                      <td className="py-2.5 px-2 text-[12.5px] text-dim">{rdv.company_name || '—'}</td>
                      <td className="py-2.5 px-2 text-[12px] text-faint hidden md:table-cell">{rdv.job_title || '—'}</td>
                      <td className="py-2.5 px-2 font-mono text-[11.5px] text-dim tabular-nums whitespace-nowrap">{fmtD(rdv.date)}</td>
                      <td className="py-2.5 px-2 font-mono text-[11px] text-faint tabular-nums whitespace-nowrap hidden sm:table-cell">{fmtD(rdv.created_at)}</td>
                      {isInternal && <td className="py-2.5 px-2 text-[12px] text-dim whitespace-nowrap">{rdv.sdr?.sales_name || '—'}</td>}
                      <td className="py-2.5 px-2"><StatusBadge status={rdv.lifecycle_status ?? null} /></td>
                      {showStatuer && (
                        <td className="py-2.5 px-2">
                          <div className="flex gap-1 flex-wrap">
                            {quick.length === 0 ? <span className="font-mono text-[10px] text-faint">—</span> : quick.map(t => (
                              <button key={t} disabled={acting === rdv.id} onClick={e => quickAct(e, rdv, t)}
                                className={`text-[10.5px] font-semibold px-2 py-0.5 rounded border disabled:opacity-40 whitespace-nowrap ${
                                  t === 'held' || t === 'qualified' ? 'border-hook-rose/40 text-hook-rose hover:bg-hook-rose/5' : 'border-line-2 text-dim hover:border-ink'}`}>
                                {QUICK_SHORT[t] ?? LIFECYCLE[t].label}
                              </button>
                            ))}
                          </div>
                        </td>
                      )}
                      <td className="py-2.5 px-2 font-mono text-[11px] text-faint text-right">›</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {addOpen && clientId && (
        <AddRdvModal defaultClientId={clientId} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />
      )}
    </div>
  );
};

export default PortalRegistrePage;
